import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import { ComponentProps } from 'react';
import { FlexContainer } from '../../../common/flex-container/flex-container.styles';
import IconButton from '../../../common/icon-button/icon-button';
import {
  FirstMile,
  LastMile,
} from '../../../models/delivery-options/delivery-miles';
import {
  DeliveryDirectionText,
  ServiceName,
} from './delivery-option-card.styles';
import { MileDetailsContainer } from './card-left/styles';
import MilesList from './miles-list/miles-list.component';
import { useIsMobile } from '../../../hooks/use-is-mobile.hook';
import BottomSidebar from '../../../common/sidebar-bottom/bottom-sidebar.component';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  usePopoverContext,
} from '../../../common/popover/popover.component';
import { useStore } from '../../../stores/store';
import { testIds } from '../../../../test-ids';
import {
  MileCarrierInfo,
  MileCarrierInfoPart,
} from './parcel-delivery-option.styles';
import Image from '../../../common/image-component/image.component';

type Mile = FirstMile | LastMile;
interface Props {
  mile: Mile;
  allMiles: Mile[];
  isCardSelected: boolean;
  onMileSelected?: (mile: Mile) => void;
  currencySymbol?: string;
  type: 'first' | 'last';
  countryLocalizedName: string | undefined;
}

const PopoverMilesList = (props: ComponentProps<typeof MilesList>) => {
  const { setOpen } = usePopoverContext();

  return (
    <MilesList
      {...props}
      onMileClick={() => {
        // eslint-disable-next-line react/destructuring-assignment
        props.onMileClick?.();
        setOpen(false);
      }}
    />
  );
};

const MileContentComponent = ({
  mile,
  allMiles,
  isCardSelected,
  onMileSelected,
  currencySymbol,
  type,
  countryLocalizedName,
}: Props) => {
  const {
    navStore: { toggleSidebarByName, closeSidebarByName },
  } = useStore();

  const { t } = useTranslation();
  const isMobile = useIsMobile();

  const isSingleMile = !allMiles || allMiles.length < 2;

  // For mobile case only show mile selector if card is selected
  const hasMileSelector = isMobile
    ? !isSingleMile && isCardSelected
    : !isSingleMile;

  const showListAsPopover = hasMileSelector && !isMobile;
  const bottomShiftPanelName =
    type === 'first'
      ? 'delivery_options_miles_mobile_fm'
      : 'delivery_options_miles_mobile_lm';

  const getTranslationKey = () => {
    if (type === 'first') {
      return mile.service.code === 'courier'
        ? 'delivery_options_send_from'
        : 'delivery_options_send_from_dropoff';
    }
    return 'delivery_options_receive_in';
  };

  const mileContent = (
    <FlexContainer
      $direction='column'
      $align='start'
      $gap='4px'>
      <DeliveryDirectionText>
        {t(getTranslationKey(), {
          country: countryLocalizedName,
        })}
      </DeliveryDirectionText>
      <FlexContainer
        $gap='1.2rem'
        $width='100%'>
        <Image
          src={mile.logo}
          alt={mile?.carrier.name ?? ''}
          height='4rem'
          width='4rem'
          borderRadius='10px'
        />
        <FlexContainer $justify='space-between'>
          <div>
            <ServiceName>{mile?.service.name}</ServiceName>
            <MileCarrierInfo>
              <MileCarrierInfoPart>{mile?.carrier.name}</MileCarrierInfoPart>
              {mile?.delivery_service?.name && (
                <MileCarrierInfoPart>
                  ({mile?.delivery_service?.name})
                </MileCarrierInfoPart>
              )}
            </MileCarrierInfo>
          </div>
          {hasMileSelector && (
            <IconButton
              testId={
                testIds.parceCreation.deliveryOptions.expandLastMilesButton
              }
              size='medium'
              appearance='secondary-filled'
              $pl='0'
              $pr='0'
              $pt='0'
              $pb='0'
              icon={{ glyph: 'chevron-down', color: 'dark' }}
            />
          )}
        </FlexContainer>
      </FlexContainer>
    </FlexContainer>
  );

  return (
    <MileDetailsContainer
      $hasMilesSelector={hasMileSelector}
      onClick={(e) => {
        if (!hasMileSelector || showListAsPopover) return;

        toggleSidebarByName(bottomShiftPanelName);
      }}>
      {!showListAsPopover && (
        <>
          {mileContent}
          {hasMileSelector && (
            <BottomSidebar
              name={bottomShiftPanelName}
              height='63.5rem'
              mobPadding='1.6rem'
              withBlurredBackground
              onClose={() => {}}>
              <FlexContainer
                $direction='column'
                $gap='1.4rem'
                $align='start'>
                <DeliveryDirectionText>
                  {t(getTranslationKey(), {
                    country: countryLocalizedName,
                  })}
                </DeliveryDirectionText>
                <MilesList
                  selectedMile={mile}
                  miles={allMiles}
                  currencySymbol={currencySymbol ?? ''}
                  onMileClick={() => closeSidebarByName(bottomShiftPanelName)}
                  onMileSelected={onMileSelected}
                />
              </FlexContainer>
            </BottomSidebar>
          )}
        </>
      )}
      {showListAsPopover && (
        <Popover
          floatingOffset={{ mainAxis: 0, crossAxis: 5 }}
          shouldMatchReferenceWidth
          placement='bottom'>
          <PopoverTrigger asChild>{mileContent}</PopoverTrigger>
          <PopoverContent isInPortal>
            <PopoverMilesList
              selectedMile={mile}
              miles={allMiles}
              currencySymbol={currencySymbol ?? ''}
              onMileSelected={onMileSelected}
            />
          </PopoverContent>
        </Popover>
      )}
    </MileDetailsContainer>
  );
};

export default observer(MileContentComponent);
